// src/services/billingService.js
import { apiRequest } from "./apiService";

const getBrowserOrigin = () => {
  if (typeof window === "undefined" || !window.location?.origin) {
    return "";
  }

  return window.location.origin;
};

const sameOriginOnly = (url) => {
  const origin = getBrowserOrigin();

  if (!origin) return null;

  try {
    const parsed = new URL(String(url || ""), origin);

    if (parsed.origin !== origin) {
      return null;
    }

    return parsed.toString();
  } catch {
    return null;
  }
};

const normalizePlanId = (payload) => {
  const p = payload && typeof payload === "object" ? payload : {};

  const value =
    p.plan_id ??
    p.planId ??
    p.price_id ??
    p.priceId ??
    p.id ??
    null;

  const normalized = String(value || "").trim();

  return normalized || null;
};

const sanitizeCheckoutPayload = (payload) => {
  const p = payload && typeof payload === "object" ? payload : {};

  return {
    plan_id: normalizePlanId(p),
    success_url: sameOriginOnly(p.success_url || p.successUrl || ""),
    cancel_url: sameOriginOnly(p.cancel_url || p.cancelUrl || ""),
  };
};

const sanitizePortalPayload = (payload) => {
  const p = payload && typeof payload === "object" ? payload : {};

  const returnUrl = sameOriginOnly(
    p.returnUrl || p.return_url || p.returnURL || ""
  );

  return returnUrl ? { returnUrl } : {};
};

// GET /billing/plans
export async function getPlans(options = {}) {
  return apiRequest("/billing/plans", "GET", null, {
    requiresAuth: false,
    ...options,
  });
}

// GET /billing/subscription
export async function getSubscription(options = {}) {
  return apiRequest("/billing/subscription", "GET", null, {
    requiresAuth: true,
    ...options,
  });
}

// POST /billing/checkout/session
export async function createCheckoutSession(payload, options = {}) {
  const safePayload = sanitizeCheckoutPayload(payload);

  if (!safePayload.plan_id) {
    throw new Error("Missing plan_id for checkout session.");
  }

  if (!safePayload.success_url || !safePayload.cancel_url) {
    throw new Error("Invalid checkout redirect URLs.");
  }

  return apiRequest("/billing/checkout/session", "POST", safePayload, {
    requiresAuth: true,
    ...options,
  });
}

// POST /billing/portal/session
export async function createPortalSession(payload = {}, options = {}) {
  const safePayload = sanitizePortalPayload(payload);

  return apiRequest("/billing/portal/session", "POST", safePayload, {
    requiresAuth: true,
    ...options,
  });
}

const billingService = {
  getPlans,
  getSubscription,
  createCheckoutSession,
  createPortalSession,
};

export default billingService;